import cn from 'classnames';
import { type ReactNode, useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { ButtonUI, CustomThemeProvider, IconTooltip, Prompt, Tag, usePortal } from 'sonata-react-components';
import { useTheme } from 'styled-components';

import './formExpandableSection.scss';

export type TagItem = {
  label: string;
};

type Props = {
  children: ReactNode;
  className?: string;
  defaultOpen?: boolean;
  hasErrors?: boolean;
  onReset: () => void;
  preffixId: string;
  tags: TagItem[];
  title: string;
  tooltipText?: string;
};

type State = {
  isOpen: boolean;
  showPrompt: boolean;
  seed: string;
};

export function FormExpandableSection({
  children,
  className,
  defaultOpen = false,
  hasErrors,
  onReset,
  tags,
  title,
  tooltipText,
  preffixId
}: Readonly<Props>) {
  const MAX_VISIBLE_TAGS = 3;
  const { formatMessage } = useIntl();
  const { name: themeName } = useTheme() as { name: string };
  const theme = themeName.toLowerCase();

  const generateSeed = () => new Date().toISOString();
  const [{ isOpen, showPrompt, seed }, setState] = useState<State>({
    isOpen: defaultOpen,
    seed: generateSeed(),
    showPrompt: false
  });

  const handleOpenToggle = () => {
    setState(prevState => ({ ...prevState, isOpen: !prevState.isOpen }));
  };

  const handleResetClick = (showPrompt: boolean) => {
    if (showPrompt) {
      setState(prevState => ({ ...prevState, showPrompt: true }));
      return;
    }
    onReset();
    setState(prevState => ({ ...prevState, seed: generateSeed(), showPrompt: false }));
  };

  const handlePromptClose = () => {
    setState(prevState => ({ ...prevState, showPrompt: false }));
  };

  const targetBodyPrompt = usePortal('formExpandableSection_prompt');

  const hasTags = tags && tags.length > 0;
  const buttonIcon = `fa-solid ${isOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`;
  const visibleTags = tags && tags.length > 0 ? tags.slice(0, MAX_VISIBLE_TAGS) : [];
  const overflowCount = visibleTags.length > 0 ? tags.length - MAX_VISIBLE_TAGS : 0;

  return (
    <>
      <div className={cn('formExpandableSection', className)} id={`${preffixId}-sectionExpandable`}>
        <div className="formExpandableSection__header" onClick={handleOpenToggle} role="button" tabIndex={0}>
          <div className="formExpandableSection__headerLeft">
            <ButtonUI icon={buttonIcon} shape="squareRounded" size="ultraSmall" type="tertiary" />
            <span className="formExpandableSection__headerLeftTitle">{title}</span>
            {tooltipText && (
              <IconTooltip
                effect="solid"
                event="mouseenter"
                icon="fa-regular fa-circle-info"
                iconColor="var(--color-sonata-primary-500) !important"
                place="top"
              >
                {tooltipText}
              </IconTooltip>
            )}
            {hasTags && !hasErrors && <i className="fa-solid fa-circle-check formExpandableSection__headerLeftIcon" />}
          </div>
          <div className="formExpandableSection__headerRight">
            {hasTags && (
              <div className="formExpandableSection__headerRightTags">
                {visibleTags.map(tag => (
                  <Tag
                    {...(!hasErrors && { icon: { webFont: 'fa-solid fa-circle-check' } })}
                    id={`${preffixId}-tag-${tag.label}`}
                    key={tag.label}
                    status={hasErrors ? 'warning' : 'category'}
                  >
                    {tag.label}
                  </Tag>
                ))}
                {overflowCount > 0 && (
                  <Tag id={`${preffixId}-tag-overflow`} status={hasErrors ? 'warning' : 'information'}>
                    {`+${overflowCount}`}
                  </Tag>
                )}
              </div>
            )}
            {hasTags && (
              <ButtonUI
                icon="fa-solid fa-rotate-left"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  handleResetClick(true);
                }}
                shape="squareRounded"
                size="ultraSmall"
                title="Reset configuration"
                type="tertiary"
              />
            )}
          </div>
        </div>
        {isOpen && (
          <div className="formExpandableSection__content" key={seed}>
            {children}
          </div>
        )}
      </div>
      {showPrompt &&
        ReactDOM.createPortal(
          <CustomThemeProvider themeContrast={theme}>
            <Prompt className="formExpandableSection__prompt" closePrompt={handlePromptClose} width="570px">
              <Prompt.Icon icon="formExpandableSection__promptIcon fas fa-exclamation-triangle" />
              <Prompt.Title variant="warning">
                {formatMessage({
                  defaultMessage: 'Are you sure you want to reset your configuration?',
                  id: 'MTFSettings.targeting.resetTitle'
                })}
              </Prompt.Title>
              <Prompt.Description variant="warning">
                {formatMessage({
                  defaultMessage: 'All current configurations in this section will be deleted.',
                  id: 'MTFSettings.targeting.resetDescription'
                })}
              </Prompt.Description>
              <Prompt.Buttons position="centered">
                <ButtonUI
                  onClick={handlePromptClose}
                  shape="squareRounded"
                  text={formatMessage({ defaultMessage: 'Cancel', id: 'common.cancel' })}
                  type="secondary"
                />
                <ButtonUI
                  onClick={() => handleResetClick(false)}
                  shape="squareRounded"
                  text={formatMessage({ defaultMessage: 'Reset', id: 'common.reset' })}
                  type="primary"
                />
              </Prompt.Buttons>
            </Prompt>
          </CustomThemeProvider>,
          targetBodyPrompt
        )}
    </>
  );
}
